import React, { useRef, useState, useEffect } from 'react';
import { useIntl, useLocation } from 'umi';
import { Icon, Layout } from 'tea-component';
import Tabs from '@intelligence/components/NewIconTabs';
import { useDeepCompareEffect } from 'ahooks';
// import SvgIcon from '@/pages/home/components/SvgIcon';
import Loading from '@/pages/intelligence/components/Loading';
import HeaderDetails from '@/pages/intelligence/components/HeaderDetails';
import Summary from './Summary';
import GamesStats from './GamesStats';
import NewsActivity from './NewsActivity';
import Financials from './Financials';
import SubsidiaryCompanies from './SubsidiaryCompanies';
import People from './People';
import SimilarCompanies from './SimilarCompanies/index';
import { fetchCompanyBasicInfo } from '@/pages/intelligence/services/companies';
import { updateDetailsPagePathURL } from './utils';
import CompanyHeader from './components/CompanyHeader';
import { buriedPointGame } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import '@/pages/intelligence/assets/scss/TopCharts/Companies/GamesDetails/index.scss';
import './index.scss';
import GameImage from '@/pages/home/components/GameImage';
import { COMPANY_GUIDE_CLASSNAMES } from '@intelligence/components/UserGuide/Company';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { getBasePath } from '@/utils/utils';
import { COMBINEID_REG } from '@intelligence/utils/constant';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import useSearchParams from '@gp/hooks/useSearchParams';
// const { Body, Sider, Content } = Layout;
const { Content } = Layout;

const NEW_MARK_IDS_MAPPING_KEY = 'CompaniesDetails';

const PAGE_LOG_CONFIG_KEYS_MAPPING: {
  [key: string]: string;
} = {
  Summary: 'companyDetailsSummary',
  NewsActivity: 'companyDetailsNewsActivity',
  // GamesStats: 'companyDetailsGamesStats',//不在页签打点，在子面签里面已打点
  Financials: 'companyDetailsFinancials',
  SubsidiaryCompanies: 'companyDetailsSubsidiaryCompanies',
  People: 'companyDetailsPeople',
  SimilarCompanies: 'companyDetailsSimilarCompanies',
};

const SUBSIDIARY_COMPANIES_TAB = {
  id: 'SubsidiaryCompanies',
  label: 'dbi_company_tab_subsidiary_companies',
  component: SubsidiaryCompanies,
};

const TABS = [
  { id: 'Summary', label: 'dbi_label_summary', component: Summary },
  {
    id: 'NewsActivity',
    label: 'dbi_company_tab_newsactivity',
    component: NewsActivity,
  },
  {
    id: 'GamesStats',
    label: 'dbi_company_tab_gamesstats',
    className: COMPANY_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT1,
    component: GamesStats,
  },
  { id: 'Financials', label: 'dbi_label_financials', component: Financials },
  { id: 'People', label: 'dbi_label_people', component: People },
  {
    id: 'SimilarCompanies',
    label: 'dbi_company_tab_similar_companies',
    component: SimilarCompanies,
  },
];

const getValidActiveId = (tabs: any[], activeId: string): string => {
  if (!Array.isArray(tabs) || !tabs.length) return '';
  const activeIds = tabs?.map((tab) => tab?.id);
  // console.log();
  return activeIds?.includes(activeId) ? activeId : tabs?.[0]?.id;
};

export default function CompaniesDetails(props: any) {
  const queryParams = props?.match?.params ?? {};
  const { dataSources } = useSearchParams();
  const activeSubSegment = props?.match?.params?.segment ?? '';

  const { formatMessage } = useIntl();
  const location = useLocation();
  const search = location?.search;

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const [tabs, setTabs] = useState([...TABS]);

  const tabsOptions = (() => {
    if (!Array.isArray(tabs)) return [];
    return tabs?.map((tab) => ({
      ...tab,
      label: formatMessage({ id: tab?.label }),
    }));
  })();

  const initialActiveId = getValidActiveId(tabs, queryParams?.tab);
  const [activeId, setActiveId] = useState(initialActiveId);

  const { resetDetailFilterExtend } = useFilterExtend({});
  useEffect(() => {
    // 离开详情时，清空详情的继承值
    return () => {
      const { pathname } = window.location; // 不要在外面取，会异步
      if (!(pathname.startsWith('/search') || pathname?.startsWith(`${getBasePath()}/search`))) {
        resetDetailFilterExtend();
      }
    };
  }, []);

  useEffect(() => {
    const activeIdTmp = getValidActiveId(tabs, queryParams?.tab);
    setActiveId(activeIdTmp);
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [queryParams?.tab]);

  const [isLoading, setIsLoading] = useState(true);
  const [companyId, setCompanyId] = useState(queryParams?.id ?? '');
  const [basicInfo, setBasicInfo] = useState<any>({});

  const initBasicInfo = async (companyId: string) => {
    setIsLoading(true);
    const res = await fetchCompanyBasicInfo({ company_id: companyId });
    setBasicInfo(res ?? {});
    setIsLoading(false);
  };

  // 是母公司则渲染Subsidiary Companies页签
  const handleIsOnSubsidiaryChange = (payload: boolean) => {
    let tabsTmp: any = [...TABS];
    const index = TABS?.findIndex((item) => item?.id === 'Financials');
    if (!payload) {
      tabsTmp = [...TABS.slice(0, index + 1), SUBSIDIARY_COMPANIES_TAB, ...TABS.slice(index + 1)];
    }
    setTabs(tabsTmp);
  };

  const refresh = (option: { companyId: string; tab: string; sbuTab: string }) => {
    const { companyId, tab } = option ?? {};
    setActiveId(tab);
    setCompanyId(companyId);
    // 添加ID校验规则
    if (COMBINEID_REG.test(companyId)) {
      initBasicInfo(companyId);
    }
  };

  const pageLog = (tab: string) => {
    const pageLogConfigKey = PAGE_LOG_CONFIG_KEYS_MAPPING?.[tab];
    const pageLogConfig = dbiPageLogMes?.[pageLogConfigKey];
    if (!pageLogConfig) return;
    if (!companyId) return;
    if (!COMBINEID_REG.test(companyId)) return; // 添加ID校验规则
    buriedPointGame(pageLogConfig, companyId, 'company_id');
  };

  useEffect(() => {
    pageLog(activeId);
  }, [activeId]);

  // 其他页面跳转过来并携带id,当前组件重新refresh，头部及时更新
  useDeepCompareEffect(() => {
    refresh({ companyId: queryParams?.id, tab: queryParams?.tab, sbuTab: activeSubSegment });
  }, [queryParams?.id]);

  // 头部吸顶相关逻辑
  const [headerFixed, setHeaderFixed] = useState(false);
  useEffect(() => {
    const handleScroll = (event: any) => {
      const { scrollTop } = event.srcElement;
      // 滚动判断数值开发调整
      if (scrollTop > 176) {
        setHeaderFixed(true);
      } else if (scrollTop < 170) {
        setHeaderFixed(false);
      }
    };
    const detailBody = document.querySelector('.dbi-companies-details');
    detailBody?.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  return (
    // dbi-companies-details这个css类名 不要删除，筛选器系统，Company新手引导 都依赖这个css类名
    <Content ref={containerRef} className="dbi-topcharts-detail dbi-companies-details">
      <HeaderDetails
        className="dbi-companies-details-header-wrapper"
        newMarkIdsMappingKey={NEW_MARK_IDS_MAPPING_KEY}
        title={formatMessage({ id: 'dbi_company_companies' })}
        tabList={tabsOptions}
        activeId={activeId}
        entityType={'company'}
        change={(tab: any) => {
          const activeIdTmp = tab?.id;
          setActiveId(activeIdTmp);
          updateDetailsPagePathURL({
            companyId,
            tab: activeIdTmp,
            search,
          });
        }}
      >
        {isLoading && (
          <div
            style={{
              height: 96,
              color: '#444',
            }}
          >
            <Loading content={formatMessage({ id: 'common_loading' })} />
          </div>
        )}
        {isLoading || (
          <CompanyHeader
            basicInfo={basicInfo}
            getIsOnSubsidiary={handleIsOnSubsidiaryChange}
            companyId={companyId}
            onCompanyIdChange={(payload: string) => {
              if (!payload) return;
              if (payload === companyId) return;
              const resetActiveId = 'Summary';
              refresh({ companyId: payload, tab: resetActiveId, sbuTab: activeSubSegment });
              updateDetailsPagePathURL({
                companyId: payload,
                tab: resetActiveId,
                sbuTab: activeSubSegment,
                search,
              });
            }}
          />
        )}
      </HeaderDetails>

      {/* 头部吸顶结构 */}
      <div
        className={`dbi-topcharts-detail-fixedheader dbi-fixedheader-wrap ${
          headerFixed ? 'visiable' : ''
        }`}
      >
        <div className="dbi-fixedheader-infobox">
          <Icon
            className="dbi-fixedheader-infobox-back"
            type="btnback"
            onClick={() => {
              window.history.back();
            }}
          />
          <GameImage
            className="dbi-fixedheader-gameinfo-avatar dbi-gameinfo-avatar--company"
            src={basicInfo?.img_url}
          />
          <div className="dbi-fixedheader-gameinfo-name">{basicInfo?.name}</div>
        </div>

        <div className="dbi-fixedheader-tabs">
          <Tabs
            ceiling
            newMarkIdsMappingKey={NEW_MARK_IDS_MAPPING_KEY}
            tabs={tabsOptions}
            animated={false}
            activeId={activeId}
            onActive={(tab: any) => {
              const activeIdTmp = tab?.id;
              setActiveId(activeIdTmp);
              updateDetailsPagePathURL({
                companyId,
                tab: activeIdTmp,
                search,
              });
            }}
          />
        </div>
      </div>

      <Content.Body full ref={scrollerRef}>
        <MarqueeBanner />
        {tabsOptions.map((tab) => (
          // @ts-ignore
          <div key={tab?.id}>
            {tab?.id === activeId && (
              <tab.component
                containerRef={containerRef}
                headerFixed={headerFixed}
                companyId={companyId}
                activeSubSegment={activeSubSegment}
                activeSegment={activeId}
                basicInfo={basicInfo}
                dataSources={dataSources === 'company_sensortower' ? 'sensortower' : 'appannie'}
              />
            )}
          </div>
        ))}
      </Content.Body>
    </Content>
  );
}
